import { showConfirm } from '../components/ConfirmDialog';
import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../hooks/useConfig';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useProfileContext } from '../hooks/useProfileContext';
import type { BacklineItem, TransportType, ProvidedBy } from '../types';
import { STAND_LABELS, isRealMicStand } from '../types';
import { excludedFromBackline } from '../lib/backlineFilter';
import ConfigPageHeader from '../components/ConfigPageHeader';
import AutoTextarea from '../components/AutoTextarea';
import NumberPicker from '../components/NumberPicker';
import BacklineImageUploader from '../components/BacklineImageUploader';
import Button from '../components/ui/Button';
import { useContentConfigPage } from '../hooks/useContentConfigPage';
import { useBacklineTypes } from '../hooks/useSuggestionList';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';
import PageLoadError from '../components/PageLoadError';
import { useConfigLoadError } from '../hooks/useConfig';
import { resolveBacklineProvider } from '../lib/backlineProvider';

/**
 * THE column grid of the whole Backline config page — declared ONCE and used by
 * the Add/Edit form, the Items List AND the read-only summary, so every column
 * lines up vertically (user 2026-10-15, twice: « les colonnes n'ont pas la même
 * taille dans item list que dans le summary », puis « les colonnes de Add
 * Backline Item sont différentes des autres »).
 *
 * 8 tracks: dots/actions │ qty │ type │ description │ notes │ provided by │
 * image │ row actions. ⚠️ Never hardcode a variant in one of the three places:
 * the number of tracks AND their widths must be identical, or the `1fr` columns
 * distribute a different remaining space and the tables read as separate grids.
 */
const BACKLINE_GRID = '70px 60px 130px minmax(0,1fr) minmax(0,1fr) 110px 110px 34px';

/**
 * Backline config page (v0.645) — one of the two pages split out of the former
 * `Audio & Backline` page. It owns the BAND-LEVEL backline:
 *   - Add / Edit a backline item
 *   - Items List
 *   - Backline summary (rider) — read-only aggregate
 *
 * The Audio specs (wireless microphones, cabling, network & power, I/O racks,
 * sidefills) live on the sibling page `ConfigAudioSpecsPage`. The per-person
 * backline stays on the musician / crew cards. The shared write gate /
 * transport / focus live in `useContentConfigPage`.
 */
export default function ConfigBacklinePage() {
  const { config, updateBackline, reload } = useConfig();
  // Lot 3a — the rider_config could not be read: the page would render EMPTY
  // fields as if the band had nothing typed. See PageLoadError.tsx.
  const configLoad = useConfigLoadError();
  const { canEditContent, readOnlyNote, transport, setTransport } = useContentConfigPage();
  const { t } = useTranslation();
  const { profiles, activeProfileId } = useProfileContext();
  // P3c: the band's own colour is DATA; only the last-resort fallback when a
  // band has none is a UI choice — the Roadcase accent, not a hardcoded hex.
  const bandColor = profiles.find((p) => p.id === activeProfileId)?.color || 'var(--accent)';
  // The Backline section of the Rider Layout is a TABLE: its `fields` are the 6
  // PDF COLUMNS (qty/type/item/model/provided/notes), not page blocks. We still
  // read the section order here to keep the rider <-> app wiring symmetric with
  // the Audio specs page; block ids that have no layout counterpart stay at
  // their default position (sortByLayoutOrder semantics).
  const layoutOrder = useLayoutOrder('backline');
  const { musicians } = useMusicians();
  const { crew } = useCrew();
  const { types: backlineTypes, put: putBacklineTypes } = useBacklineTypes();

  // Auto-add a backline type typed by hand into the shared suggestion list, so
  // it becomes available everywhere. (2026-08-13 — the standalone suggestions
  // editor was removed in favour of this; mirrors the musician/crew cards.)
  const ensureBacklineType = (t: string) => {
    const v = (t || '').trim();
    if (!v) return;
    if (!backlineTypes.some((x) => x.toLowerCase() === v.toLowerCase())) {
      void putBacklineTypes([...backlineTypes, v]);
    }
  };

  const backline = config.backline[transport];

  // Page-level populate: ONE confirmation, then copy the Backline section
  // (items only) from the chosen mode into the current one.
  const populateAll = async (src: TransportType) => {
    if (!(await showConfirm(t('configBackline.populateAllConfirm', { src })))) return;
    updateBackline({ [transport]: { ...config.backline[src] } } as any);
  };

  // --- Backline item editor (local form state) ---
  const [editingId, setEditingId] = useState<string | null>(null);
  const [itemName, setItemName] = useState('');
  const [itemType, setItemType] = useState('other');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  // « Provided by » (band / venue) of the band-level item — the SAME attribute
  // the musician/crew cards carry (BacklineEditor) and the rider PDF prints in
  // its Provided-by column. Defaults to `band`: a band-level item is, by
  // construction, gear the BAND brings on top of the per-person cards
  // (asked by user 2026-10-15 — it used to be impossible to set here).
  const [itemProvider, setItemProvider] = useState<ProvidedBy>('band');

  const handleAddItem = () => {
    if (!itemName.trim()) return;
    const newItem: BacklineItem = {
      id: editingId || Date.now().toString(),
      type: itemType,
      label: itemName,
      quantity,
      notes,
      imageUrl: imageUrl || undefined,
      providedBy: itemProvider,
    };
    const updated = editingId
      ? backline.items.map((item) => (item.id === editingId ? newItem : item))
      : [...backline.items, newItem];
    updateBackline({ [transport]: { ...backline, items: updated } } as any);
    clearForm();
  };

  const handleDeleteItem = (id: string) => {
    const updated = backline.items.filter((item) => item.id !== id);
    updateBackline({ [transport]: { ...backline, items: updated } } as any);
  };

  const handleEditItem = (item: BacklineItem) => {
    setEditingId(item.id);
    // Legacy items saved as { name, itemType, details } before the schema moved
    // to { label, type, notes } — map them so editing keeps the old values.
    setItemName((item as any).label || (item as any).name || '');
    setItemType(((item as any).itemType || item.type || 'other') as string);
    setQuantity(item.quantity);
    setNotes(item.notes || (item as any).details || '');
    setImageUrl(item.imageUrl || '');
    setItemProvider((item.providedBy as ProvidedBy) || 'band');
  };

  const clearForm = () => {
    setEditingId(null);
    setItemName('');
    setItemType('other');
    setQuantity(1);
    setNotes('');
    setImageUrl('');
    setItemProvider('band');
  };

  // --- Aggregated backline summary (read-only) ---
  // The rider PDF's Backline table aggregates the SELECTED musicians' and crew
  // cards' backline, plus the band-level Items List below. Show the same
  // aggregated view here, using the SAME row layout & fields as the musician
  // card (qty / type / label / notes) so the site stays homogeneous.
  // (Restored 2026-08-13 — was silently reduced to band-level items only.)
  const selMus = new Set(config.rider?.activeMusicianIds || []);
  const selCrew = new Set(config.rider?.activeCrewIds || []);
  // RÉSUMÉ MIC STANDS (lecture seule, 2026-10-17) : les pieds fournis par la salle,
  // agrégés depuis les pistes des personnes SÉLECTIONNÉES (musiciens + crew) —
  // le même agrégat que la section Mic Stands du rider PDF (isRealMicStand, jamais un clip).
  const micStandMap = new Map<string, { label: string; qty: number; withoutBoom: boolean }>();
  for (const m of [...(musicians || []), ...(crew || [])]) {
    if (!selMus.has(String((m as any).id)) && !selCrew.has(String((m as any).id))) continue;
    for (const inst of (m as any).instruments || []) {
      for (const track of inst.tracks || []) {
        if (track.providedBy !== 'venue') continue;
        if (!isRealMicStand(track.stand)) continue;
        const key = `${track.stand}_${track.withoutBoom ? 'nobo' : 'boom'}`;
        const existing = micStandMap.get(key);
        if (existing) existing.qty += 1;
        else micStandMap.set(key, { label: STAND_LABELS[track.stand as keyof typeof STAND_LABELS] || track.stand, qty: 1, withoutBoom: !!track.withoutBoom });
      }
    }
  }
  const micStandRows = [...micStandMap.values()];
  const summaryMap = new Map<string, { label: string; type: string; qty: number; notes: string; providedBy: ProvidedBy; imageUrl?: string; colorCounts: Map<string, number> }>();
  const collectSummary = (items: any[], sourceColor?: string) => {
    for (const item of items || []) {
      // Same exclusion as the rider PDF backline table (2026-10-18): wedges/
      // subs → Monitors section, mic stands → Mic Stands section. This table
      // MIRRORS the PDF, so it must not show the duplicates either.
      if (excludedFromBackline((item as any).type, (item as any).label || (item as any).name)) continue;
      // Description = the item's OWN label only (2026-10-15): the Type column is
      // always printed now, so falling back to the type repeated it (see the PDF
      // reference — « retirer les descriptions qui sont une copie du type »).
      const label = (item as any).label || (item as any).name || ((item as any).type ? '' : t('configAudioBackline.item'));
      // The EFFECTIVE provider for THIS transport — the same resolver the rider
      // PDF uses (plane/train force `venue`). It belongs to the merge KEY too:
      // the PDF keys its rows by provider, so merging a band-provided and a
      // venue-provided item of the same label here would print one row with the
      // wrong provider.
      const providedBy = resolveBacklineProvider(item, transport);
      const key = `${(item as any).type || 'other'}_${label}_${providedBy}`;
      const n = (item as any).quantity || 1;
      const existing = summaryMap.get(key);
      if (existing) {
        existing.qty += n;
        if (sourceColor) existing.colorCounts.set(sourceColor, (existing.colorCounts.get(sourceColor) || 0) + n);
      } else {
        const colorCounts = new Map<string, number>();
        if (sourceColor) colorCounts.set(sourceColor, n);
        summaryMap.set(key, {
          label,
          type: (item as any).type || '',
          qty: n,
          notes: (item as any).notes || (item as any).details || '',
          providedBy,
          imageUrl: (item as any).imageUrl,
          colorCounts,
        });
      }
    }
  };
  for (const m of musicians) if (selMus.has(m.id)) collectSummary(m.backline || [], m.color);
  for (const c of crew) if (selCrew.has(c.id)) collectSummary(c.backline || [], c.color);
  // Band-level items (the editable Items List) come on top of the cards — their
  // color dot is the band's color.
  collectSummary(backline.items, bandColor);
  const summaryRows = [...summaryMap.values()];

  // The page blocks. `backline.item` matches a real Rider Layout id (the Item
  // column) so the layout editor's "edit" link can deep-link + flash it; the
  // Add / summary blocks have no layout counterpart and stay in default order.
  const blocks = [
    {
      id: 'backline.add',
      render: () => (
        <div className="form-section">
          <h2>{editingId ? t('configAudioBackline.editItem') : t('configAudioBackline.addItem')}</h2>
          <p style={{ marginTop: -8, marginBottom: 12, color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {t('configAudioBackline.addItemHint')}
          </p>

          <div className="backline-list">
            <div className="backline-row backline-edit-grid" style={{ gridTemplateColumns: BACKLINE_GRID }}>
              <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                <Button disabled={!canEditContent} variant="primary" size="sm" onClick={handleAddItem} aria-label={editingId ? t('configAudioBackline.updateItem') : t('configAudioBackline.addItemAria')}>
                  {editingId ? '✏️' : '➕'}
                </Button>
                {editingId && (
                  <Button disabled={!canEditContent} variant="ghost" size="sm" onClick={clearForm} aria-label={t('configAudioBackline.cancel')}>
                    ✕
                  </Button>
                )}
              </div>
              <NumberPicker disabled={!canEditContent}
                value={quantity}
                options={[1, 2, 3, 4, 5, 6, 8, 10]}
                onChange={(v) => setQuantity(v || 1)}
                className="backline-qty"
              />
              <input disabled={!canEditContent}
                type="text"
                list="config-backline-types-suggestions"
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
                onBlur={(e) => ensureBacklineType(e.target.value)}
                placeholder={t('configAudioBackline.typePh')}
                className="backline-type"
              />
              <AutoTextarea disabled={!canEditContent}
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder={t('configAudioBackline.descPh')}
                className="backline-label"
              />
              <AutoTextarea disabled={!canEditContent}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('configAudioBackline.notesPh')}
                className="backline-notes"
              />
              {/* Provided by — band or venue, exactly like a musician/crew card
                  item (BacklineEditor). Without it a band-level item could only
                  ever be "band", which is wrong for shared gear the venue must
                  supply (user 2026-10-15). */}
              <select disabled={!canEditContent}
                value={itemProvider}
                onChange={(e) => setItemProvider(e.target.value as ProvidedBy)}
                title={t('backlineEditor.providedTitle')}
                aria-label={t('backlineEditor.providedBy')}
                className="backline-provider td-input"
              >
                <option value="band">{t('backlineEditor.band')}</option>
                <option value="venue">{t('backlineEditor.venue')}</option>
              </select>
              <BacklineImageUploader disabled={!canEditContent} imageUrl={imageUrl} onChange={(v) => setImageUrl(v ?? '')} />
            </div>
          </div>
          {/* datalist must live OUTSIDE the grid (a grid child would become a column) */}
          <datalist id="config-backline-types-suggestions">
            {backlineTypes.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
      ),
    },
    {
      id: 'backline.item',
      render: () => backline.items.length > 0 ? (
        // Items List — rendered as the SAME table as the summary below
        // (asked by user 2026-10-15). The old `item-card` markup had NO CSS at
        // all: only the bold description and a bare "×N" were visible, and the
        // TYPE — the very thing that tells a mic stand from a drum riser — was
        // never shown. Same 5 cells as the summary (dots / qty / type /
        // description / notes) + one narrow actions cell for ✏️ / 🗑️.
        <div className="form-section" data-field-id="backline.item">
          <h2>{t('configAudioBackline.itemsList', { count: backline.items.length })}</h2>
          <div className="backline-list">
            {backline.items.map((item) => {
              // Description = the item's OWN label only, exactly like the summary
              // and the rider PDF (2026-10-15): the Type column is always printed
              // now, so falling back to the type repeated it on the printed rider.
              const label = (item as any).label || (item as any).name || ((item as any).type ? '' : t('configAudioBackline.item'));
              const type = (((item as any).itemType || item.type || '') as string);
              const notes = item.notes || (item as any).details || '';
              const qty = item.quantity || 1;
              return (
                <div key={item.id} className="backline-row backline-items-grid" style={{ gridTemplateColumns: BACKLINE_GRID }}>
                  <span title={`${qty} × ${bandColor}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', alignContent: 'center', justifyContent: 'center', gap: 3, minHeight: 34, padding: '4px 0' }}>
                    {Array.from({ length: qty }).map((_, k) => (
                      <span key={k} title={bandColor} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: bandColor, border: '1px solid var(--border)', flexShrink: 0, marginRight: 2 }} />
                    ))}
                  </span>
                  <span className="backline-qty" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, textAlign: 'center' }}>×{qty}</span>
                  <span className="backline-type" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>{type || '—'}</span>
                  <span className="backline-label" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{label}</span>
                  <span className="backline-notes" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{notes}</span>
                  {/* The STORED value (what you set, and what ✏️ re-loads) — the
                      summary below shows the EFFECTIVE value for this transport
                      instead, exactly like the rider PDF. */}
                  <span className="backline-provider" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>{item.providedBy === 'venue' ? t('backlineEditor.venue') : t('backlineEditor.band')}</span>
                  {/* Reference photo, exactly like a musician/crew card row —
                      the Items List owns an imageUrl (Add form uploader) and the
                      PDF prints it in the Backline table. */}
                  <span className="backline-image" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 34 }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)' }} />
                    ) : null}
                  </span>
                  <span className="backline-actions">
                    <button disabled={!canEditContent} onClick={() => handleEditItem(item)} className="btn-icon" aria-label={t('configAudioBackline.editItem')}>
                      ✏️
                    </button>
                    <button disabled={!canEditContent} onClick={() => handleDeleteItem(item.id)} className="btn-icon btn-danger" aria-label={t('common.delete')}>
                      🗑️
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null,
    },
    {
      id: 'backline.summary',
      render: () => summaryRows.length > 0 ? (
        // Aggregated backline summary (read-only) — same layout & fields as the
        // musician/crew card's Backline section (qty / type / label / notes).
        <div className="form-section">
          <h2>{t('configAudioBackline.backlineSummary')}</h2>
          <p style={{ marginTop: -8, marginBottom: 10, color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {t('configAudioBackline.backlineSummaryHint')}
          </p>
          <div className="backline-list">
            {/* ⚠️ The summary declares the SAME 7-track template as the Items
                List above — trailing actions track INCLUDED, left empty here.
                Without it the two `1fr` columns compute ~40 px narrower in the
                Items List (its 7th track takes the space) and the two tables no
                longer line up (user 2026-10-15: « les colonnes n'ont pas la même
                taille dans item list que dans le summary »). */}
            {summaryRows.map((row, i) => (
              <div key={i} className="backline-row backline-summary-grid" style={{ gridTemplateColumns: BACKLINE_GRID }}>
                <span title={[...row.colorCounts.entries()].map(([c, n]) => `${n} × ${c}`).join(', ')} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', alignContent: 'center', justifyContent: 'center', gap: 3, minHeight: 34, padding: '4px 0' }}>
                  {[...row.colorCounts.entries()].map(([c, n]) => (
                    <span key={c}>
                      {Array.from({ length: n }).map((_, k) => (
                        <span key={k} title={c} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c, border: '1px solid var(--border)', flexShrink: 0, marginRight: 2 }} />
                      ))}
                    </span>
                  ))}
                </span>
                <span className="backline-qty" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, textAlign: 'center' }}>×{row.qty}</span>
                <span className="backline-type" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>{row.type || '—'}</span>
                <span className="backline-label" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{row.label}</span>
                <span className="backline-notes" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{row.notes || ''}</span>
                <span className="backline-provider" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>{row.providedBy === 'venue' ? t('backlineEditor.venue') : t('backlineEditor.band')}</span>
                {/* Reference photo — the same one the PDF prints in the Backline
                    table (a musician's uploaded model photo rides through here). */}
                <span className="backline-image" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 34 }}>
                  {row.imageUrl ? (
                    <img src={row.imageUrl} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)' }} />
                  ) : null}
                </span>
                {/* 8th track stays EMPTY: it is the row-actions column of the
                    Items List, declared here too so both grids match exactly. */}
              </div>
            ))}
          </div>
        </div>
      ) : null,
    },
    {
      // RÉSUMÉ LECTURE SEULE (2026-10-17, user : « un résumé des mic stands dans
      // backline, juste pour visualiser ») — les pieds fournis par la salle,
      // agrégés depuis les pistes des personnes SÉLECTIONNÉES. Rien n'est édité ici.
      id: 'backline.micstands',
      render: () => micStandRows.length > 0 ? (
        <div className="form-section">
          <h2>{t('configAudioBackline.micStandsSummary')}</h2>
          <p style={{ marginTop: -8, marginBottom: 10, color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {t('configAudioBackline.micStandsSummaryHint')}
          </p>
          <div className="backline-list">
            {micStandRows.map((s, i) => (
              <div key={i} className="backline-row" style={{ gridTemplateColumns: 'minmax(0,1fr) 90px 90px' }}>
                <span className="backline-label" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{s.label}</span>
                <span className="backline-qty" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34 }}>×{s.qty}</span>
                <span className="backline-type" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34 }}>{s.withoutBoom ? t('pdf.misc.noBoom') : 'Boom'}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null,
    },
  ];

  const sortedBlocks = sortByLayoutOrder(blocks, layoutOrder, (b) => b.id);


  // Lot 3a: the rider_config could not be read → empty fields would read as
  // "this band typed nothing". Show the shared state instead; the writes stay
  // gated by `useConfig` skipSave, so a retry cannot wipe anything.
  if (configLoad.failed) {
    return (
      <PageLoadError
        title={t('configBackline.title')}
        detail={configLoad.message}
        onRetry={() => void reload().catch(() => undefined)}
      />
    );
  }

  return (
    <div className="page config-page" data-config-page="backline" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configBackline.title"
        title={t('configBackline.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />
      <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('configBackline.subtitle')}</p>

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {sortedBlocks.map((b) => <Fragment key={b.id}>{b.render()}</Fragment>)}
      </div>
    </div>
  );
}
